/***
 * Created on 2025/7/15
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/

import React, {useState, useRef} from 'react';
import ModalComponent from "../common/ModalComonent/ModalComponent";
import {useTranslation} from "react-i18next";
import ic_info_inline_svg from "../../static/img/utils/ic-info-inline.svg";
import MultiSelectInputComponent from "../common/Form/MultiSelectInputComponent";
import useInputHandler from "../../hook/InputHandlerHook";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useOrganizationApi} from "../../hook/OrganizationAPIHook";
import {useAlertContext} from "../../context/AlertContext";
import {invalidateAccessGrant} from "../../service/accessGrantSync";

const USER_ROLE = {
    owner: {
        name: 'Owner',
        description: 'Full control over the organization, members, roles, licenses and devices.',
        settings: {
            organization_settings: {
                users_and_groups: {status: "enabled"},
                devices_and_licenses: {status: "enabled"},
                sites: {status: "enabled"},
                organization_info: {status: "enabled"}
            },
            cloud_space_management: {
                storage_space: {status: "enabled"},
                license_seats: {status: "enabled"},
                all_shared_spaces: {status: "enabled"}
            }
        }
    },
    user_manager: {
        name: 'User Manager',
        description: 'Can invite users, manage members and groups. Cannot change or assign Owner roles.',
        settings: {
            organization_settings: {
                users_and_groups: {status: "enabled"},
                devices_and_licenses: {status: "withInfo", info: 'Access depends on which devices this member is assigned to manage.'},
                sites: {status: "disabled"},
                organization_info: {status: "disabled"}
            },
            cloud_space_management: {
                storage_space: {status: "disabled"},
                license_seats: {status: "disabled"},
                all_shared_spaces: {status: "withInfo", info: 'Access depends on which shared spaces this member is allowed to view or manage.'}
            }
        }
    },
    member: {
        name: 'Member',
        description: 'No user management rights. Other permissions are granted separately below.',
        settings: {
            organization_settings: {
                users_and_groups: {status: "disabled"},
                devices_and_licenses: {status: "withInfo", info: 'Access depends on which devices this member is assigned to manage.'},
                sites: {status: "disabled"},
                organization_info: {status: "disabled"}
            },
            cloud_space_management: {
                storage_space: {status: "disabled"},
                license_seats: {status: "disabled"},
                all_shared_spaces: {status: "withInfo", info: 'Access depends on which shared spaces this member is allowed to view or manage.'}
            }
        }
    },
}

/**
 * Pure helper: computes the {role, site_admin, device_admin} delta.
 * Only non-locked devices are considered — locked (site-cascaded) devices
 * must never appear in either add or remove.
 */
export function buildPermissionDelta({initialSiteIds, initialDeviceIds, selectedSites, selectedDevices, role}) {
    const initSiteSet = new Set(initialSiteIds);
    const initDeviceSet = new Set(initialDeviceIds);

    const currentSiteIds = selectedSites.filter(s => s.checked).map(s => s.value.id);
    const currentDeviceIds = selectedDevices.filter(d => d.checked && !d.locked).map(d => d.value.id);

    const currentSiteSet = new Set(currentSiteIds);
    const currentDeviceSet = new Set(currentDeviceIds);

    return {
        role,
        site_admin: {
            add: currentSiteIds.filter(id => !initSiteSet.has(id)),
            remove: initialSiteIds.filter(id => !currentSiteSet.has(id)),
        },
        device_admin: {
            add: currentDeviceIds.filter(id => !initDeviceSet.has(id)),
            remove: initialDeviceIds.filter(id => !currentDeviceSet.has(id)),
        },
    };
}

function PermissionTag({label, permission}) {
    const {t} = useTranslation();
    return (
        <span className={`tag tag-permission tag-permission${permission?.status==='disabled' ? '' : '--active'} tag-rounded tag-tiny ${permission?.status==='withInfo' ? 'tag-opt' : ''}`}>
            <span className="tag-label">{label}</span>
            {
                permission?.status === 'withInfo' &&
                <span className="ic-tag-info w-14px line-height-0 ml-4 tooltip">
                    <img className="w-100" src={ic_info_inline_svg} alt=""/>
                    <span className="tooltiptext">{t(`@${permission?.info}@`)}</span>
                </span>
            }
        </span>
    );
}

function UserRolePermissionModal({isOpen, onClose, user_data, state, organization_id, doRefresh}) {
    const {t} = useTranslation();
    const alert = useAlertContext();
    const {OrganizationAPI} = useOrganizationApi();
    const queryClient = useQueryClient();
    const [selectedSites, setSelectedSites] = useState(() => {
        const manage_site = Object.keys(state?.row_data?.permissions?.manage_site || []).length > 0 ? state?.row_data?.permissions?.manage_site : [];
        const list = state?.sites?.map(item => ({checked: manage_site?.includes(item.id), value: item}));
        return list;
    });
    const [selectedDevices, setSelectedDevices] = useState(() => {
        // const list = state?.devices?.map(item => ({checked: true, value: item}));
        const list = state?.devices?.map(item => {
            const isfixed = Object.values(state?.row_data?.permissions?.manage_device?.fixed || {}).some(site => site?.includes(item.id));
            const isChecked = state?.row_data?.permissions?.manage_device?.user_assigned?.includes(item.id) || isfixed;
            return {checked: isChecked, value: item, locked: isfixed};
        });
        return list;
    });
    // Capture initial selection once (at dialog open) so that toggling does not
    // mutate the baseline used by buildPermissionDelta.
    const initialSiteIdsRef = useRef(
        (state?.sites || [])
            .filter(item => (state?.row_data?.permissions?.manage_site || []).includes(item.id))
            .map(item => item.id)
    );
    const initialDeviceIdsRef = useRef(
        (state?.devices || [])
            .filter(item => {
                const isfixed = Object.values(state?.row_data?.permissions?.manage_device?.fixed || {}).some(site => site?.includes(item.id));
                const isChecked = state?.row_data?.permissions?.manage_device?.user_assigned?.includes(item.id) || isfixed;
                // Only non-locked (user_assigned) devices count in the baseline.
                return isChecked && !isfixed;
            })
            .map(item => item.id)
    );

    const [input, setInput, handleInputChange] = useInputHandler({
        initial: {
            role: {value: state?.row_data?.role ? state?.row_data?.role : state?.row_data?.is_owner ? 'owner' : 'member', error: null, required: true},
            device_admin: {
                value: state?.row_data?.role === 'owner' || ((state?.row_data?.permissions?.manage_device?.user_assigned || [])?.length > 0 || Object.values(state?.row_data?.permissions?.manage_device?.fixed || []).some(site=> site?.length > 0)),
                error: null,
                required: false
            },
            site_admin: {value: state?.row_data?.role === 'owner' || state?.row_data?.permissions?.manage_site?.length > 0, error: null, required: false}
        }
    });
    const [refreshSite, setRefreshSite] = useState(0);
    const [refreshDevice, setRefreshDevice] = useState(0);

    const {data: orgGroups} = useQuery({
        queryKey: [`organization/${organization_id}/group`, 'membership-check'],
        queryFn: async () => {
            const res = await OrganizationAPI.get(`organization/${organization_id}/group`, {limit: 200, gt: true});
            return res?.result?.groups || res?.result || [];
        },
        enabled: !!organization_id && isOpen,
    });

    const belongsToAnyGroup = orgGroups
        ? orgGroups.some(g => (g?.users || []).some(u => u?.id === state?.row_data?.id))
        : (state?.row_data?.groups || []).length > 0;

    const handlePermissionToggle = (e) => {
        const {name, checked} = e.currentTarget;
        if (!checked) {
            if (name === 'site_admin') {
                setSelectedSites(prev => prev.map(item => ({...item, checked: false})));
                setRefreshSite(prev => prev + 1);
                setSelectedDevices(prev => prev.map(item => ({...item, checked: !item.locked, locked: false})));
                setRefreshDevice(prev => prev + 1);
                setInput(prevInput => {
                    return {
                        ...prevInput,
                        site_admin: {...prevInput.site_admin, value: false, error: null},
                        device_admin: {...prevInput.device_admin, value: selectedDevices.some(item => item.checked && !item.locked), error: null}
                    }
                });
            }
            else if (name === 'device_admin') {
                setSelectedDevices(prev => prev.map(item => ({...item, checked: item.locked, locked: item.locked})));
                setRefreshDevice(prev => prev + 1);
                setInput(prevInput => {
                    return {
                        ...prevInput,
                        device_admin: {...prevInput.device_admin, value: selectedDevices.some(item => item.locked), error: null}
                    }
                });
            }
        }
        else {
            handleInputChange(e);
        }
    };

    const onRoleChanged = (e) => {
        const role = e.currentTarget.value;
        setInput(prevInput => ({
            ...prevInput,
            role: {value: role, error: null, required: true},
            device_admin: {
                value: role === 'owner' || ((state?.row_data?.permissions?.manage_device?.user_assigned || [])?.length > 0 || Object.values(state?.row_data?.permissions?.manage_device?.fixed || []).some(site=> site?.length > 0)),
                error: null,
                required: false
            },
            site_admin: {value: role === 'owner' || state?.row_data?.permissions?.manage_site?.length > 0, error: null, required: false}
        }));
    }

    const onSiteAdminChangedCallback = (action, payload, extra) => {
        console.log('onSiteAdminChangedCallback', action, payload, extra);
        let site_devices = [];
        let isChecked = false;
        if (action === 'onCheckboxChange') {
            const [site, checked] = payload;
            site_devices = site?.devices?.map(device => device.id) || [];
            isChecked = checked;
        }
        else if (['selectAll', 'deselectAll'].includes(action)) {
            const sites = extra?.full_list || [];
            isChecked = action === 'selectAll';
            sites.forEach(site => {
                site_devices = site_devices.concat(site?.value?.devices?.map(device => device.id) || []);
            });
        }

        if (isChecked) {
            setInput(prevInput => ({
                ...prevInput,
                device_admin: {...prevInput.device_admin, value: true},
            }));
        }

        setRefreshDevice(prev => {
            setSelectedDevices(prevDevices => {
                return prevDevices.map(device => {
                    if (site_devices && site_devices?.length > 0 && site_devices.includes(device.value.id)) {
                        return {
                            ...device,
                            checked: isChecked,
                            locked: isChecked
                        };
                    }
                    return device;
                });
            });
            return prev + 1;
        });
    }

    const assignPermMutation = useMutation({
        mutationFn: async (data) => await OrganizationAPI.post(`organization/${organization_id}/user/${state?.row_data?.id}/permissions`, null, data),
        onSuccess: (envelope) => {
            console.log('assignPermMutation success', envelope);
            // OrganizationAPI.post returns the full {code, message, result} envelope.
            const result = envelope?.result;
            const status = result?.status;
            const failed = result?.failed || [];

            if (status === 'partial' || failed.length > 0) {
                // Partial success — keep dialog open and surface the failures.
                const failedIds = failed.map(f => f.device_id || f.id || JSON.stringify(f)).join(', ');
                alert(true, 'error', 'partial', t('@Some permissions could not be applied. Failed devices: {{ids}}@', {ids: failedIds}), 10 * 1000);
                return;
            }

            invalidateAccessGrant(queryClient, organization_id);
            doRefresh(prev => prev + 1);
            onClose();
            alert(true, 'success', 'ok', t('@Access permissions for {{display_name}} have been updated.@', {display_name: state?.row_data?.display_name}), 10 * 1000);
        },
        onError: (error) => {
            console.error('assignPermMutation error', error);
            // Surface the backend error to the user (e.g. 1060011 last-owner guard).
            // Dialog stays open (onClose is only called on success) so they can adjust.
            const code = error?.response?.data?.code;
            alert(true, 'error', code, t('@Failed to update role and permissions. Please try again.@'), 10 * 1000);
        }
    });

    const onApply = async () => {
        // User Manager cannot assign 'owner' role
        if (state?.currentUserRole === 'user_manager' && input.role.value === 'owner') {
            return;
        }
        const delta = buildPermissionDelta({
            initialSiteIds: initialSiteIdsRef.current,
            initialDeviceIds: initialDeviceIdsRef.current,
            selectedSites,
            selectedDevices,
            role: input.role.value,
        });
        await assignPermMutation.mutateAsync(delta);
    }

    // User Manager operators cannot hold or grant Site/Device Admin, so hide that section for them.
    const isOperatorUserManager = state?.currentUserRole === 'user_manager';

    return (
        <ModalComponent title={t('@Assign Role and Permissions for ({{display_name}})@', {display_name: state?.row_data?.display_name})} isOpen={isOpen} onClose={onClose} okText={t('@Apply@')} onSubmit={onApply}>
            <div className="dialog-content">
                <div className="text-wrap">
                    <div className="sub-head">{t('@Role@')}</div>
                    <div className="indent-l-16">{t('@Choose a primary role for this member.@')}</div>
                </div>
                <div className="role-card-list">
                    {
                        Object.entries(USER_ROLE).map(([key, role]) => {
                            const isCurrentUserManager = state?.currentUserRole === 'user_manager';
                            const isRoleDisabled = isCurrentUserManager && key === 'owner';
                            return (
                            <div className="role-card" key={key}>
                                <div className="comp-radio-group">
                                    <div className="comp-radio">
                                        <input type="radio" id={`rb-${key}`} name="role" onChange={onRoleChanged} checked={input['role'].value===key} value={key} disabled={isRoleDisabled}/>
                                        <label htmlFor={`rb-${key}`} className="comp-label"></label>
                                    </div>
                                    <div className="val-field">
                                        <span className="val-field__text fs14 fw5">{t(`@${role.name}@`)}</span>
                                    </div>
                                </div>
                                <div className="fs13">{t(`@${role.description}@`)}</div>
                            </div>
                        );})
                    }
                </div>
                <div className="role-permission-mapping">
                    <div>{t('@Allowed to Manage or Access:@')}</div>
                    <div className="role-permission-mapping__category">
                        <div className="fs12 fw5 mb-10">{t('@Organization Settings@')}</div>
                        <div className="display-flex flex-wrap flex-gap-6">
                            <PermissionTag label={t('@Users & Groups@')} permission={USER_ROLE[input?.['role'].value]?.settings?.organization_settings?.users_and_groups}/>
                            <PermissionTag label={t('@Devices & Licenses@')} permission={USER_ROLE?.[input['role'].value]?.settings?.organization_settings?.devices_and_licenses}/>
                            <PermissionTag label={t('@Sites@')} permission={USER_ROLE[input?.['role'].value]?.settings?.organization_settings?.sites}/>
                            <PermissionTag label={t('@Organization Info@')} permission={USER_ROLE[input?.['role'].value]?.settings?.organization_settings?.organization_info}/>
                        </div>
                    </div>
                    <div className="role-permission-mapping__category">
                        <div className="fs12 fw5 mb-10">{t('@Cloud Space Management@')}</div>
                        <div className="display-flex flex-wrap flex-gap-6">
                            <PermissionTag label={t('@Storage Space@')} permission={USER_ROLE[input['role'].value]?.settings?.cloud_space_management?.storage_space}/>
                            <PermissionTag label={t('@License Seats@')} permission={USER_ROLE[input['role'].value]?.settings?.cloud_space_management?.license_seats}/>
                            <PermissionTag label={t('@All Shared Spaces@')} permission={USER_ROLE[input['role'].value]?.settings?.cloud_space_management?.all_shared_spaces} />
                        </div>
                    </div>
                </div>
                {!isOperatorUserManager && (
                <>
                <div className="text-wrap">
                    <div className="sub-head">{t('@Site and Device Permissions@')}</div>
                    <div className="indent-l-16">{t('@Grant additional permissions for managing devices or sites.@')}</div>
                    <div className="indent-l-16">
                        <div className="dialog-info-block dialog-info-block--info mt-10">
                            <div className="dialog-info-block__ic"><img src={ic_info_inline_svg} alt=""/></div>
                            <div className="dialog-info-block__text">
                                <div>{t('@Organization Owners are automatically Site and Device administrators and cannot be removed.@')}</div>
                            </div>
                        </div>
                    </div>
                    {belongsToAnyGroup && (
                        <div className="indent-l-16">
                            <div className="dialog-info-block dialog-info-block--info mt-10" role="status">
                                <div className="dialog-info-block__ic"><img src={ic_info_inline_svg} alt=""/></div>
                                <div className="dialog-info-block__text">
                                    <div>{t('@Checkboxes here control individual access only. This user belongs to Groups that may also grant access. To fully revoke all access, please go to the Groups page.@')}</div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="device-permission-settings">
                    <div className={`device-permission-settings__category ${input?.role?.value === 'owner' ? 'lock' : ''}`}>
                        <div className="comp-checkbox">
                            <input type="checkbox" id="cb-t1"
                                   checked={input?.site_admin?.value}
                                   disabled={input?.role?.value==='owner'}
                                   name='site_admin'
                                   value={input?.site_admin?.value}
                                   onChange={handlePermissionToggle}/>
                            <label htmlFor="cb-t1" className="comp-label"><span className="fw5">{t('@Site Admin@')}</span></label>
                        </div>
                        <div className="device-permission-settings__category__desc">{t('@Manages all devices assigned to selected Sites and automatically receives permission for newly assigned devices.@')}</div>
                        <div className="device-permission-settings__category__select">
                            <MultiSelectInputComponent
                                component_style={{width: '100%', maxWidth: '100%'}}
                                selectedItems={selectedSites}
                                setSelectedItems={setSelectedSites}
                                placeholder_text={input?.role?.value==='owner' ? t('@All sites@') : t('@Select one or more Sites@')}
                                callbackDispatch={onSiteAdminChangedCallback}
                                refresh={refreshSite}
                                disabled={input?.role?.value === 'owner' ? true : !input?.site_admin.value}/>
                        </div>
                    </div>
                    <div className={`device-permission-settings__category ${input?.role?.value === 'owner' ? 'lock' : ''}`}>
                        <div className="comp-checkbox">
                            <input type="checkbox"
                                   id="cb-t2"
                                   checked={input?.device_admin?.value}
                                   disabled={input?.role?.value==='owner'}
                                   name='device_admin'
                                   value={input?.site_admin?.value}
                                   onChange={handlePermissionToggle}/>
                            <label htmlFor="cb-t2" className="comp-label"><span className="fw5">{t('@Device Admin@')}</span></label>
                        </div>
                        <div className="device-permission-settings__category__desc">{t('@Can manage specific devices assigned by an Owner or Site Administrator, including applying licenses and configuration.@')}</div>
                        <div className="device-permission-settings__category__select">
                            <MultiSelectInputComponent
                                component_style={{width: '100%', maxWidth: '100%'}}
                                selectedItems={selectedDevices}
                                setSelectedItems={setSelectedDevices}
                                placeholder_text={input?.role?.value==='owner' ? t('@All devices@') : t('@Select one or more Devices@')}
                                refresh={refreshDevice}
                                disabled={input?.role?.value === 'owner' ? true : !input?.device_admin?.value}/>
                        </div>
                    </div>
                </div>
                </>
                )}
            </div>
        </ModalComponent>
    );
}

export default UserRolePermissionModal;